import { Button } from "@svea/design-system-components-react";
import {
	type AnyQuestion,
	ButtonGroup,
	getQuestionnaireInitialValues,
	Heading,
	parseFormValues,
	Question,
	type QuestionnaireFormValues,
	Stack,
	useText,
} from "@sveabank/cc-common";
import { Form } from "react-final-form";
import Card from "../../../../components/card";
import { usePageContext } from "../../../../hooks/context";
import type {
	ClcBeneficialOwnerAnswerPostReq,
	ClcBeneficialOwnerAnswerSet,
	ClcBeneficialOwnerQuestionSet,
} from "../../../../types/clc-application";
import { useClcCompanyApplication } from "../../hooks";

type Props = {
	onSubmit: (values: ClcBeneficialOwnerAnswerPostReq) => Promise<void>;
	onCancel: () => void;
};

const ClcPepQuestions = ({ onSubmit, onCancel }: Props) => {
	const {
		beneficialOwnerQuestions,
		sortedCountries,
		clcApplicationDetails: {
			beneficialOwners: { value: beneficialOwnersValue },
		},
	} = usePageContext(useClcCompanyApplication);

	const [t, texts] = useText();

	const prependNationalId =
		(nationalId: string) =>
		<T extends AnyQuestion>(question: T): T => ({
			...question,
			answerChoices: question.answerChoices.map((a) => ({
				...a,
				subQuestions: a.subQuestions.map(prependNationalId(nationalId)),
			})),
			questionId: `${nationalId}--${question.questionId}`,
		});

	const questionSets: Array<ClcBeneficialOwnerQuestionSet> = beneficialOwnerQuestions.map(
		({ nationalId, questions }) =>
			({
				nationalId: nationalId,
				questions: questions.map(prependNationalId(nationalId)),
			}) satisfies ClcBeneficialOwnerQuestionSet,
	);

	const submitForm = (values: QuestionnaireFormValues) => {
		// TODO: Remove the exclamation mark here when someone feels like figuring out what this does
		const nationalIds = new Set<string>(Object.entries(values).map(([key]) => key.split("--")[0]!));

		const answeredQuestions = parseFormValues(
			questionSets.flatMap((qs) => qs.questions),
			values,
		);

		const [firstAnswer, ...restAnswers] = Array.from(nationalIds).map(
			(nationalId) =>
				({
					nationalId,
					questions: Object.entries(answeredQuestions)
						.filter(([_key, value]) => value.questionId.startsWith(nationalId))
						.flatMap(([_key, value]) => {
							const questionId = value.questionId.split("--")[1];
							if (!questionId) throw new Error("Missing questionId");

							return {
								questionId,
								answers: value.answers,
								question: value.question,
							};
						}),
				}) satisfies ClcBeneficialOwnerAnswerSet,
		);

		if (!firstAnswer) throw new Error("First answer is missing");

		onSubmit({
			...firstAnswer,
			additional: restAnswers.length > 0 ? restAnswers : null,
		} satisfies ClcBeneficialOwnerAnswerPostReq);
	};

	const initialValues = getQuestionnaireInitialValues(questionSets.flatMap((qs) => qs.questions));

	return (
		<Form<QuestionnaireFormValues>
			onSubmit={submitForm}
			initialValues={initialValues}
			render={({ handleSubmit, submitting }) => (
				<form onSubmit={handleSubmit}>
					<Stack gap={4} align="start" grow fullSize>
						{questionSets.map((questionSet) => {
							const beneficialOwner = beneficialOwnersValue?.beneficialOwners.find(
								(bo) => bo.nationalId === questionSet.nationalId,
							);
							return (
								<Card key={questionSet.nationalId} classNames="bg-background-secondary">
									<Stack gap={2} component="article">
										<Heading.H3
											italic={false}
											size="h4"
										>{`${beneficialOwner?.firstName} ${beneficialOwner?.lastName} - ${beneficialOwner?.nationalId}`}</Heading.H3>

										<Stack gap={6}>
											{questionSet.questions.map((q) => (
												<Question question={q} key={q.questionId} allCountries={sortedCountries} />
											))}
										</Stack>
									</Stack>
								</Card>
							);
						})}
						<ButtonGroup row align="start">
							<Button type="button" variant="secondary" onPress={onCancel}>
								{t(texts.common.abort)}
							</Button>
							<Button
								type="submit"
								isPending={submitting}
								data-tracking-action="CLC Application Continue"
								data-tracking-name="PEP Questions"
							>
								{t(texts.common.save)}
							</Button>
						</ButtonGroup>
					</Stack>
				</form>
			)}
		/>
	);
};

export default ClcPepQuestions;
